import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
const RING_COUNT = isMobile ? 3 : 8;
const STREAK_COUNT = isMobile ? 0 : 10;
const COLORS = ['#FF00FF', '#FF1493', '#FF69B4', '#FFB6C1', '#FFD1DC', '#C71585', '#FF69B4', '#FF1493'];

const PortalTransition = ({ onComplete }) => {
  const calledRef = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!calledRef.current) { calledRef.current = true; onComplete(); }
    }, isMobile ? 1200 : 2200);
    return () => clearTimeout(timer);
  }, [onComplete]);

  const done = () => { if (!calledRef.current) { calledRef.current = true; onComplete(); } };

  // Mobile: simple fast fade — no heavy animations
  if (isMobile) {
    return (
      <motion.div
        className="fixed inset-0 z-[100] flex items-center justify-center"
        style={{ background: '#0A0208' }}
        data-testid="portal-transition"
        initial={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        {[0, 1, 2].map(i => (
          <motion.div
            key={`ring-${i}`}
            className="absolute rounded-full"
            style={{ border: `2px solid ${COLORS[i]}` }}
            initial={{ width: 20, height: 20, opacity: 0 }}
            animate={{ width: [20, 800], height: [20, 800], opacity: [0, 0.5, 0] }}
            transition={{ duration: 0.9, delay: i * 0.1, ease: 'easeOut' }}
          />
        ))}
        <motion.div
          className="absolute rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(255,209,220,0.8) 0%, rgba(255,0,255,0.3) 50%, transparent 100%)' }}
          initial={{ width: 0, height: 0, opacity: 0 }}
          animate={{ width: [0, 600], height: [0, 600], opacity: [0, 0.7, 1] }}
          transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
          onAnimationComplete={done}
        />
        <motion.div className="absolute inset-0" style={{ background: '#1A0510' }}
          initial={{ opacity: 0 }} animate={{ opacity: [0, 0, 1] }}
          transition={{ duration: 1, ease: 'easeInOut' }}
        />
        <motion.p
          className="absolute z-10 font-heading text-2xl text-white uppercase tracking-[0.2em]"
          style={{ textShadow: '0 0 20px rgba(255,0,255,0.7)' }}
          initial={{ opacity: 0 }} animate={{ opacity: [0, 1, 0] }}
          transition={{ duration: 1 }}
        >ENTER</motion.p>
      </motion.div>
    );
  }

  // Desktop: full experience
  return (
    <motion.div
      className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden"
      style={{ background: '#0A0208' }}
      data-testid="portal-transition"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6 }}
    >
      {[...Array(RING_COUNT)].map((_, i) => (
        <motion.div
          key={`ring-${i}`}
          className="absolute rounded-full"
          style={{ border: `2px solid ${COLORS[i % COLORS.length]}`, boxShadow: `0 0 15px ${COLORS[i % COLORS.length]}30` }}
          initial={{ width: 10 + i * 4, height: 10 + i * 4, opacity: 0, rotate: i * 20 }}
          animate={{ width: [10 + i * 4, 1800], height: [10 + i * 4, 1800], opacity: [0, 0.6, 0.5, 0], rotate: [i * 20, i * 20 + 45] }}
          transition={{ duration: 1.8, delay: i * 0.1, ease: [0.4, 0, 0.2, 1] }}
        />
      ))}

      {[...Array(STREAK_COUNT)].map((_, i) => (
        <motion.div
          key={`streak-${i}`}
          className="absolute"
          style={{ width: '1.5px', height: '60px', background: `linear-gradient(to bottom, transparent, ${COLORS[i % COLORS.length]}60, transparent)`, transformOrigin: 'center center', transform: `rotate(${(i / STREAK_COUNT) * 360}deg)` }}
          initial={{ opacity: 0, scaleY: 0 }}
          animate={{ opacity: [0, 0.5, 0], scaleY: [0, 5, 10] }}
          transition={{ duration: 1.4, delay: 0.15 + (i % 4) * 0.04, ease: 'easeOut' }}
        />
      ))}

      <motion.div
        className="absolute rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(255,209,220,0.9) 0%, rgba(255,0,255,0.4) 40%, rgba(139,0,139,0.15) 70%, transparent 100%)' }}
        initial={{ width: 0, height: 0, opacity: 0 }}
        animate={{ width: [0, 80, 2200], height: [0, 80, 2200], opacity: [0, 0.8, 0.9, 1] }}
        transition={{ duration: 1.6, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
        onAnimationComplete={done}
      />

      <motion.div className="absolute inset-0" style={{ background: '#1A0510' }}
        initial={{ opacity: 0 }} animate={{ opacity: [0, 0, 0, 1] }}
        transition={{ duration: 2, delay: 0.2, ease: 'easeInOut' }}
      />

      <motion.div className="absolute z-10 text-center"
        initial={{ opacity: 0, scale: 0.7 }}
        animate={{ opacity: [0, 1, 1, 0], scale: [0.7, 1, 1.1, 1.4] }}
        transition={{ duration: 1.6, delay: 0.15, ease: 'easeOut' }}
      >
        <p className="font-heading text-3xl md:text-5xl text-white uppercase tracking-[0.3em]"
          style={{ textShadow: '0 0 30px rgba(255,0,255,0.7)' }}>ENTER</p>
      </motion.div>
    </motion.div>
  );
};

export default PortalTransition;
